// © Copyright 2026 Emman Consulting SAS
import { useState, useRef, useEffect } from "react";

/**
 * Reusable long-press hook for sticker dots.
 * - Short tap  → onTap(code)
 * - Long press → onReset(code) after `delay` ms
 *
 * Returns { handlers } — spread onto the target element.
 */
export function useLongPress({ onTap, onReset, delay = 800 } = {}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const timerRef = useRef(null);
  const isLongRef = useRef(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const start = (e, code) => {
    if (e.type === "mousedown") e.preventDefault();
    if (isProcessing) return;
    isLongRef.current = false;
    timerRef.current = setTimeout(() => {
      isLongRef.current = true;
      setIsProcessing(true);
      onReset?.(code);
      setTimeout(() => setIsProcessing(false), 300);
    }, delay);
  };

  const end = (e, code) => {
    e.preventDefault();
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!isLongRef.current && !isProcessing) {
      setIsProcessing(true);
      onTap?.(code);
      setTimeout(() => setIsProcessing(false), 300);
    }
  };

  const cancel = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const handlers = (code) => ({
    onTouchStart: (e) => start(e, code),
    onTouchEnd:   (e) => end(e, code),
    onTouchMove:  cancel,
    onMouseDown:  (e) => start(e, code),
    onMouseUp:    (e) => end(e, code),
    onMouseLeave: cancel,
  });

  return { handlers, isProcessing };
}
